import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faCopy, faCheck, faSpinner, faPen, faBoxArchive,
  faVideo, faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { formatRelativeDate, formatForDateInput } from '../../../../utils/dateUtils';
import { supabase } from '../../../../lib/supabase';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useTheme } from '../../../../context/ThemeContext';
import { Avatar, Button, Dropdown, PulseDot, SidePanel, Tabs, useToast } from '../../../ui';
import CommsEventChip from '../../../comms/CommsEventChip';
import SpecQueueFromTarget from '../../../ai/SpecQueueFromTarget';
import AiTaskJobs from '../../../ai/AiTaskJobs';
import { TASK_STATUSES, taskStatusMeta } from './taskMeta';

interface TaskComment {
  id: string;
  content: string;
  created_by: string | null;
  created_at: string;
  user?: {
    name: string | null;
    email: string;
    avatar?: string | null;
  } | null;
}

/** Shape the panel needs from a task — structurally compatible with both the
 * client-detail task rows and FulfillmentHQ's Task. Keep loose on purpose. */
export interface TaskDetailTask {
  id: string;
  title?: string;
  status?: string;
  due_date?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  description?: string | null;
  video_url?: string | null;
  website_id?: string;
  website_title?: string;
}

interface TaskEditState {
  title: string;
  status: string;
  dueDate: string | null;
  assignedTo: string | null;
  assignedToName?: string | null;
  website_id?: string;
  website_title?: string;
}

interface TaskDetailSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTask: TaskDetailTask | null;
  users: { id: string; name: string; email: string }[];
  /**
   * Persist the full edit state. The drawer autosaves: it calls this on every
   * field change, optimistically, and expects the promise to REJECT on failure
   * so it can revert the field and toast. Hosts must not close the panel here.
   */
  onUpdateTask: (taskId: string, updatedTask: TaskEditState) => Promise<void>;
  onArchiveTask?: (taskId: string) => Promise<void>;
}

/** Debounce for title keystrokes before an autosave fires. Blur saves at once. */
const TITLE_SAVE_MS = 600;

function EyebrowRow({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
      }}
    >
      <span className="t-eyebrow">{label}</span>
      {children}
    </div>
  );
}

function QuietIconButton({
  icon,
  title,
  color,
  onClick,
}: {
  icon: typeof faCopy;
  title: string;
  color?: string;
  onClick: () => void;
}) {
  return (
    <button
      className="pressable hover-wash"
      title={title}
      aria-label={title}
      onClick={onClick}
      style={{
        border: 'none',
        background: 'transparent',
        color: color || 'var(--text-faint)',
        cursor: 'pointer',
        padding: '3px 6px',
        borderRadius: 'var(--radius-sm)',
        display: 'inline-flex',
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: '12px' }} />
    </button>
  );
}

/** "Sep 12" from a yyyy-mm-dd input value, without a timezone shift. */
function formatDueLabel(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return format(new Date(y, m - 1, d, 12), 'MMM d');
}

export default function TaskDetailSidepanel({
  isOpen,
  onClose,
  selectedTask,
  users,
  onUpdateTask,
  onArchiveTask,
}: TaskDetailSidepanelProps) {
  // Quill needs an explicit dark-theme class (third-party widget, not tokenized)
  const { theme } = useTheme();
  const toast = useToast();
  const [editingTask, setEditingTask] = useState<TaskEditState | null>(null);
  const [videoUrlCopied, setVideoUrlCopied] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [activeTab, setActiveTab] = useState<'Details' | 'Comments'>('Details');
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [aiJobsTick, setAiJobsTick] = useState(0);

  const taskId = selectedTask?.id ?? null;

  // Latest props/state without re-running effects or re-creating callbacks.
  const taskRef = useRef(selectedTask);
  taskRef.current = selectedTask;
  /** Last state the database is known to hold (or is being written). */
  const savedRef = useRef<TaskEditState | null>(null);
  /** Live title draft, so a debounced save reads what was actually typed. */
  const titleRef = useRef('');
  const titleTimer = useRef<number | null>(null);

  // Fetch comments for the task
  const fetchComments = useCallback(async () => {
    if (!taskId) return;

    try {
      setIsLoadingComments(true);

      // First fetch comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('task_comments')
        .select('id, content, created_by, created_at')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;

      if (!commentsData || commentsData.length === 0) {
        setComments([]);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(commentsData.map((comment) => comment.created_by))].filter((id): id is string => Boolean(id));

      // Fetch user data separately from public.users table
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, avatar')
        .in('id', userIds);

      if (usersError) {
        console.warn('Could not fetch user data for comments:', usersError);
      }

      // Map user data to comments
      const commentsWithUsers = commentsData.map((comment) => {
        const user = usersData?.find((u) => u.id === comment.created_by);
        return {
          ...comment,
          user: user ? { name: user.name, email: user.email, avatar: user.avatar } : null,
        };
      });

      setComments(commentsWithUsers);
    } catch (err) {
      console.error('Error fetching task comments:', err);
    } finally {
      setIsLoadingComments(false);
    }
  }, [taskId]);

  // (Re)initialise the edit state when a different task opens. Keyed on the id
  // (not the object) so a host re-rendering the same task after one of our own
  // saves does not wipe a half-typed title, the open tab, or a comment draft.
  useEffect(() => {
    if (titleTimer.current) {
      window.clearTimeout(titleTimer.current);
      titleTimer.current = null;
    }

    const task = taskRef.current;
    if (task && isOpen) {
      const initial: TaskEditState = {
        title: task.title || '',
        status: task.status || 'Scheduled',
        dueDate: task.due_date || null,
        assignedTo: task.assigned_to || null,
        assignedToName: task.assigned_to_name,
        website_id: task.website_id,
        website_title: task.website_title,
      };
      setEditingTask(initial);
      savedRef.current = initial;
      titleRef.current = initial.title;

      // Fetch comments for the task
      fetchComments();
    } else {
      savedRef.current = null;
    }

    // Reset description editing state
    setIsEditingDescription(false);
    setEditedDescription(task?.description || '');
    setActiveTab('Details'); // Reset to details tab when new task is selected
    setNewComment('');
  }, [taskId, isOpen, fetchComments]);

  useEffect(() => {
    return () => {
      if (titleTimer.current) window.clearTimeout(titleTimer.current);
    };
  }, []);

  /**
   * Autosave one field. Optimistic: the UI updates first, the host writes the
   * row, and on failure the field reverts and an error toast explains.
   */
  const persist = useCallback(
    async (patch: Partial<TaskEditState>, label: string) => {
      const id = taskRef.current?.id;
      const previous = savedRef.current;
      if (!id || !previous) return;

      const next: TaskEditState = { ...previous, ...patch };
      savedRef.current = next;
      setEditingTask((t) => (t ? { ...t, ...patch } : t));

      try {
        await onUpdateTask(id, next);
        toast.success(label);
      } catch (err) {
        console.error('Error autosaving task:', err);
        // Only the keys this save touched go back; a newer save may own the rest.
        const revert: Partial<TaskEditState> = {};
        for (const key of Object.keys(patch) as (keyof TaskEditState)[]) {
          (revert as Record<string, unknown>)[key] = previous[key];
        }
        savedRef.current = { ...(savedRef.current || next), ...revert };
        if (revert.title !== undefined) titleRef.current = revert.title;
        setEditingTask((t) => (t ? { ...t, ...revert } : t));
        toast.error(`${label} didn't save`, 'Reverted. Check your connection and try again.');
      }
    },
    [onUpdateTask, toast]
  );

  /**
   * A job just went onto the Spec queue. The ai_jobs_start_task trigger has already
   * moved a Scheduled or Blocked task to In Progress; mirror it here so the board
   * row updates and the next autosave doesn't write the old status back.
   */
  const handleEnqueued = useCallback(() => {
    setAiJobsTick((n) => n + 1);
    const status = savedRef.current?.status;
    if (status === 'Scheduled' || status === 'Blocked') {
      persist({ status: 'In Progress' }, 'Status → In Progress');
    }
  }, [persist]);

  /** Save the title draft now (blur, Enter, close) or from the debounce. */
  const flushTitle = useCallback(
    (fromBlur = false) => {
      if (titleTimer.current) {
        window.clearTimeout(titleTimer.current);
        titleTimer.current = null;
      }
      const draft = titleRef.current.trim();
      const current = savedRef.current?.title ?? '';
      if (!draft) {
        // Empty is never saved. On blur, put the old title back and say so.
        if (fromBlur && current) {
          titleRef.current = current;
          setEditingTask((t) => (t ? { ...t, title: current } : t));
          toast.error("Title can't be empty", 'Kept the previous title.');
        }
        return;
      }
      if (draft === current) return;
      persist({ title: draft }, 'Title saved');
    },
    [persist, toast]
  );

  const handleTitleChange = (value: string) => {
    titleRef.current = value;
    setEditingTask((t) => (t ? { ...t, title: value } : t));
    if (titleTimer.current) window.clearTimeout(titleTimer.current);
    titleTimer.current = window.setTimeout(() => flushTitle(false), TITLE_SAVE_MS);
  };

  const handleClose = () => {
    flushTitle(true);
    onClose();
  };

  // Handle submitting a new comment
  const handleSubmitComment = async () => {
    if (!newComment.trim() || !selectedTask?.id) return;

    setIsSubmittingComment(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('No authenticated user found');
      }

      const { error } = await supabase.from('task_comments').insert([
        {
          task_id: selectedTask.id,
          content: newComment.trim(),
          created_by: session.user.id,
        },
      ]);

      if (error) throw error;

      setNewComment('');
      fetchComments(); // Refresh comments
    } catch (err) {
      console.error('Error submitting comment:', err);
      toast.error("Comment didn't post", 'Try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!selectedTask?.id) return;

    setIsSavingDescription(true);

    try {
      const { error } = await supabase
        .from('client_tasks')
        .update({ description: editedDescription || null })
        .eq('id', selectedTask.id);

      if (error) throw error;

      // Update the selected task with the new description
      selectedTask.description = editedDescription;

      setIsEditingDescription(false);
      toast.success('Description saved');
    } catch (error) {
      console.error('Error saving description:', error);
      toast.error("Description didn't save", 'Your edit is still in the editor. Try again.');
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleCancelDescription = () => {
    setEditedDescription(selectedTask?.description || '');
    setIsEditingDescription(false);
  };

  const handleArchiveTask = async () => {
    if (!selectedTask?.id || !onArchiveTask) return;

    setIsArchiving(true);

    try {
      await onArchiveTask(selectedTask.id);
      onClose(); // Close the sidepanel after successful archive
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error("Task wasn't archived", 'Try again.');
    } finally {
      setIsArchiving(false);
      setArchiveModalOpen(false);
    }
  };

  const handleCopyVideoUrl = () => {
    if (!selectedTask?.video_url) return;

    navigator.clipboard
      .writeText(selectedTask.video_url)
      .then(() => {
        setVideoUrlCopied(true);
        setTimeout(() => setVideoUrlCopied(false), 2000);
      })
      .catch((err) => console.error('Failed to copy video URL:', err));
  };

  if (!selectedTask) return null;

  const isLoomVideo = selectedTask.video_url?.includes('loom.com');
  const embedUrl = isLoomVideo ? selectedTask.video_url?.replace('/share/', '/embed/') : selectedTask.video_url;

  return (
    <>
      <SidePanel
        open={isOpen}
        onClose={handleClose}
        width={480}
        eyebrow={selectedTask.website_title ? `Task · ${selectedTask.website_title}` : 'Task'}
        title="Edit Task"
        footer={
          <>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Changes save as you make them.
            </span>
            <span style={{ flex: 1 }} />
            {onArchiveTask ? (
              <Button
                variant="ghost"
                onClick={() => setArchiveModalOpen(true)}
                style={{ color: 'var(--danger-400)' }}
              >
                <FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: '12px', marginRight: '7px' }} />
                Archive
              </Button>
            ) : null}
          </>
        }
      >
        <div key={selectedTask.id} className="ck-stagger">
          {/* seamless title — saves on pause or blur */}
          <input
            value={editingTask?.title || ''}
            onChange={(e) => handleTitleChange(e.target.value)}
            onBlur={() => flushTitle(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            placeholder="Task title..."
            aria-label="Task title"
            className="ck-title-input"
          />

          {/* tabs */}
          <div style={{ margin: '14px 0 18px' }}>
            <Tabs tabs={['Details', 'Comments']} active={activeTab} onChange={(t) => setActiveTab(t as 'Details' | 'Comments')} />
          </div>

          {activeTab === 'Details' ? (
            <div className="ck-stagger">
              {/* linked comms event (renders nothing when the task has none) */}
              <CommsEventChip
                taskId={selectedTask.id}
                style={{ marginBottom: '18px' }}
                onEnqueued={handleEnqueued}
              />
              <AiTaskJobs
                key={selectedTask.id}
                taskId={selectedTask.id}
                taskTitle={editingTask?.title || selectedTask.title || ''}
                refreshKey={aiJobsTick}
                onEnqueued={handleEnqueued}
                style={{ marginBottom: '18px' }}
              />

              {/* status chip rail — click saves */}
              <div style={{ marginBottom: '18px' }}>
                <EyebrowRow label="Status" />
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {TASK_STATUSES.map((status) => {
                    const active = editingTask?.status === status;
                    const meta = taskStatusMeta(status);
                    return (
                      <button
                        key={status}
                        className="pressable"
                        aria-pressed={active}
                        onClick={() => {
                          if (active) return;
                          persist({ status }, `Status → ${status}`);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '7px 12px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid ' + (active ? 'var(--border-strong)' : 'var(--border-hairline)'),
                          background: active ? 'var(--surface-active)' : 'transparent',
                          color: active ? 'var(--text-body)' : 'var(--text-muted)',
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: (active
                            ? 'var(--weight-medium)'
                            : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                          cursor: 'pointer',
                          transition:
                            'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
                        }}
                      >
                        <PulseDot tone={meta.tone} pulse={false} />
                        {status}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* meta grid — each control saves on change */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  marginBottom: '20px',
                }}
              >
                <Dropdown
                  label="Assignee"
                  options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: u.id, label: u.name }))]}
                  value={editingTask?.assignedTo || ''}
                  onChange={(e) => {
                    const userId = e.target.value || null;
                    if ((editingTask?.assignedTo || null) === userId) return;
                    const userName = userId ? users.find((u) => u.id === userId)?.name || null : null;
                    persist(
                      { assignedTo: userId, assignedToName: userName },
                      userId ? `Assigned to ${userName || 'teammate'}` : 'Unassigned'
                    );
                  }}
                />
                <div>
                  <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Due</div>
                  <input
                    type="date"
                    value={formatForDateInput(editingTask?.dueDate)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (formatForDateInput(editingTask?.dueDate) === value) return;
                      persist({ dueDate: value || null }, value ? `Due ${formatDueLabel(value)}` : 'Due date cleared');
                    }}
                    className="ck-date-input"
                  />
                </div>
              </div>

              {/* description */}
              <div style={{ marginBottom: '20px' }}>
                <EyebrowRow label="Description">
                  {!isEditingDescription && selectedTask.id ? (
                    <SpecQueueFromTarget
                      taskId={selectedTask.id}
                      title={selectedTask.title || ''}
                      cardId={null}
                      trigger="bolt"
                      onEnqueued={handleEnqueued}
                    />
                  ) : null}
                  <QuietIconButton
                    icon={isEditingDescription ? faTimes : faPen}
                    title={isEditingDescription ? 'Cancel' : 'Edit description'}
                    onClick={() => {
                      if (isEditingDescription) {
                        handleCancelDescription();
                      } else {
                        setIsEditingDescription(true);
                      }
                    }}
                  />
                </EyebrowRow>

                {isEditingDescription ? (
                  <div>
                    <div
                      className={theme === 'dark' ? 'quill-editor-dark' : ''}
                      style={{
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border-hairline)',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden',
                      }}
                    >
                      <ReactQuill
                        value={editedDescription}
                        onChange={setEditedDescription}
                        theme="snow"
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            ['link'],
                            ['clean'],
                          ],
                        }}
                        placeholder="Add task description..."
                        style={{ minHeight: '120px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                      <Button size="sm" variant="ghost" onClick={handleCancelDescription} disabled={isSavingDescription}>
                        Cancel
                      </Button>
                      <Button size="sm" variant="secondary" onClick={handleSaveDescription} disabled={isSavingDescription}>
                        {isSavingDescription ? 'Saving...' : 'Save Description'}
                      </Button>
                    </div>
                  </div>
                ) : selectedTask.description ? (
                  <div
                    className="prose prose-invert max-w-none ck-task-description"
                    style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.55 }}
                    dangerouslySetInnerHTML={{ __html: selectedTask.description }}
                  />
                ) : (
                  <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                    No description yet.
                  </p>
                )}
              </div>

              {/* walkthrough video */}
              {selectedTask.video_url ? (
                <div style={{ marginBottom: '8px' }}>
                  <EyebrowRow label="Walkthrough" />
                  <div
                    className="hover-wash"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '11px',
                      padding: '9px 10px',
                      margin: '0 -10px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <FontAwesomeIcon icon={faVideo} style={{ fontSize: '13px', width: '16px', color: 'var(--text-faint)' }} />
                    <span
                      className="t-num"
                      style={{
                        flex: 1,
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {selectedTask.video_url}
                    </span>
                    <QuietIconButton
                      icon={videoUrlCopied ? faCheck : faCopy}
                      title={videoUrlCopied ? 'Copied' : 'Copy video URL'}
                      color={videoUrlCopied ? 'var(--status-up)' : undefined}
                      onClick={handleCopyVideoUrl}
                    />
                    <a
                      href={selectedTask.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pressable hover-wash"
                      title="Open video"
                      aria-label="Open video"
                      style={{
                        color: 'var(--text-faint)',
                        padding: '3px 6px',
                        borderRadius: 'var(--radius-sm)',
                        display: 'inline-flex',
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '12px' }} />
                    </a>
                  </div>

                  {isLoomVideo ? (
                    <div
                      style={{
                        marginTop: '10px',
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden',
                        border: '1px solid var(--border-hairline)',
                      }}
                    >
                      <div className="aspect-video">
                        <iframe
                          src={embedUrl?.split('?')[0]}
                          frameBorder="0"
                          allowFullScreen
                          className="w-full h-full"
                          title="Walkthrough video"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="ck-stagger">
              {/* comments */}
              {isLoadingComments ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 0 16px',
                    color: 'var(--text-faint)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" style={{ fontSize: '12px' }} />
                  Loading comments...
                </div>
              ) : comments.length === 0 ? (
                <p style={{ margin: '4px 0 16px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                  No comments yet. Notes for the team land here.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '18px' }}>
                  {comments.map((cm) => {
                    const authorName = cm.user?.name || cm.user?.email?.split('@')[0] || 'User';
                    return (
                      <div key={cm.id} style={{ display: 'flex', gap: '11px' }}>
                        <Avatar name={authorName} src={cm.user?.avatar} size={26} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px', marginBottom: '3px' }}>
                            <span
                              style={{
                                fontSize: 'var(--text-sm)',
                                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                                color: 'var(--text-body)',
                              }}
                            >
                              {authorName}
                            </span>
                            <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                              {formatRelativeDate(cm.created_at)}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            {cm.content}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment about this task..."
                  rows={3}
                  className="ck-textarea"
                />
                <div style={{ marginTop: '8px' }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleSubmitComment}
                    disabled={!newComment.trim() || isSubmittingComment}
                  >
                    {isSubmittingComment ? 'Adding...' : 'Comment'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </SidePanel>

      {/* Archive Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={handleArchiveTask}
        title="Archive Task"
        message={`Are you sure you want to archive "${selectedTask?.title}"? You can restore it from the archived tasks panel.`}
        isDeleting={isArchiving}
        confirmButtonLabel="Archive"
        processingLabel="Archiving..."
        titleIcon={faBoxArchive}
        confirmButtonIcon={faBoxArchive}
      />
    </>
  );
}
